/* eslint-disable react/require-default-props */
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer, Icon, Inputs, ModalContext, Text } from '@taxdown/piccolo-web';
import cardValidator from 'card-validator';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { setIsLoadingAction, setIsNotLoadingAction } from 'Store/modules/app/actions';
import { useTranslation } from 'Hooks/useTranslation';
import useValidateClabe, { getErrorContext } from 'Hooks/useValidateClabe';
import { CLABE_HELP_URL } from 'Constants/helpsUrl';
import { openLinkInNewTab } from 'Utils/links';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import { useFetchNewSeasons, usePatchClabeUser } from './hooks';

const DashboardClabeForm = ({ clabeUser = '' }: { clabeUser?: string }): JSX.Element => {
  const { t } = useTranslation(locizeNS.MODALS);
  const [clabeAccount, setClabeAccount] = useState('');
  const { isValid, error, bankTag, isLoading } = useValidateClabe(clabeAccount);
  const dispatch = useDispatch();
  const userStage = useSelector(userStageSelector);
  const [previousClabeUser, setPreviousClabeUser] = useState<string>('');
  const { hideModal } = useContext(ModalContext);
  const { mutate, isSuccess, isError } = usePatchClabeUser();
  useFetchNewSeasons(isSuccess, userStage, hideModal);

  useEffect(() => {
    if (clabeUser) {
      setPreviousClabeUser(clabeUser);
    }
  }, [clabeUser]);

  useEffect(() => {
    if (isError) {
      dispatch(setIsNotLoadingAction());
    }
  }, [isError]);

  const handleChange = (val): void => {
    setClabeAccount(val.replace(/\s+/g, ''));
  };

  const submitClabe = (): void => {
    dispatch(setIsLoadingAction());
    mutate({ CLABE: clabeAccount });
  };

  const getError = (): boolean => {
    if (cardValidator.number(clabeAccount).isValid) {
      return true;
    }
    if (clabeAccount === previousClabeUser && clabeUser !== '') return true;
    return clabeAccount ? !isValid : false;
  };

  return (
    <FlexContainer direction="column" gap="dimension_16">
      <img src={`${TAXDOWN_ASSETS_DOMAIN}/mgm/CLABE.svg`} alt="clabe-modal-icon" />
      <Text color="dark" type="headline" size="XS_L_S" align="center">
        {t('Confirm.Clabe.Title')}
      </Text>
      <Text color="grey" type="paragraphs" size="XS_M_S" align="center">
        {t('Confirm.Clabe.Subtitle')}
      </Text>
      <Text color="fresh" type="paragraphs" size="XS_L_S" align="center">
        {t('Confirm.Clabe.banks')}
      </Text>
      {clabeUser && (
        <Inputs.Text label={t('Confirm.Clabe.Previous.label')} value={previousClabeUser} readOnly />
      )}
      <Inputs.Text
        label={t('Confirm.Clabe.label')}
        value={clabeAccount}
        hasError={!isLoading && getError()}
        onChange={handleChange}
        onHelpButton={() => openLinkInNewTab(CLABE_HELP_URL)}
        helpButtonUrl={CLABE_HELP_URL}
        placeholder={t('filing:Filing.clabe.placeholder')}
        errorMsg={
          clabeAccount === previousClabeUser
            ? t(`Clabe.same.error`)
            : t(`filing:Filing.clabe.${getErrorContext(clabeAccount, error)}`)
        }
      />
      {clabeAccount && isValid && (
        <FlexContainer gap="dimension_08" align="center">
          <Icon icon="IconInfoLineSDark" color="default" />
          <Text color="dark" type="paragraphs" size="S_M_L">
            {t('filing:Filing.clabe.tag', {
              info: bankTag,
            })}
          </Text>
        </FlexContainer>
      )}
      <FlexContainer align="center" justify="center">
        <Buttons.Classic
          disabled={!isValid || isLoading || clabeAccount === previousClabeUser}
          label={t('ClabeModal.CTA')}
          mode="primary"
          size="m"
          onClick={submitClabe}
        />
      </FlexContainer>
    </FlexContainer>
  );
};

export default DashboardClabeForm;
